import { useIASMA } from "@/contexts/IASMAContext";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNotify } from "@/contexts/NotifyContext";
import axios from "axios";
import { Alumni, AlumniExtended } from "iasma-datatypes";
import { queryAlumni, queryAlumniList } from "@/services/DirectoryService";
import { CheckCheck, ChevronDown, ChevronUp, GraduationCap, School, X } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Button } from "../ui/button";
import ViewMerge from "./ViewMerge";
import { useNavigate } from "react-router-dom";

interface FIELD { key: keyof AlumniExtended; label: string; mono?: boolean }

const theme = {
	ground: "#F4EFE4",
	ink: "#1E2A38",
	inkSoft: "#4A5768",
	line: "#DCD3BE",
	card: "#FFFEFB",
	brass: "#A9782F",
	brassSoft: "#E7D9B8",
	good: "#3E6B4F",
	goodSoft: "#E1EBE2",
	bad: "#9C4028",
	badSoft: "#F3E1DA",
	amber: "#B98A2E",
	amberSoft: "#F3E7CE",
};

const FIELDS: FIELD[] = [
	{ key: "Name", label: "Nama" },
	{ key: "Graduation", label: "Angkatan" },
	{ key: "Gender", label: "Jenis Kelamin", mono: true },
	{ key: "Religion", label: "Agama" },
	{ key: "NIS", label: "NIS" },
	{ key: "Classes", label: "Kelas" },
	{ key: "Status", label: "Status" },
	{ key: "Alias", label: "Pangilan" },
	{ key: "Title", label: "Gelar" },
	{ key: "BirthDate", label: "Tanggal Lahir", mono: true },
	{ key: "DOD", label: "Tanggal Kematian" },
	{ key: "Phones", label: "Phone", mono: true },
	{ key: "ExtraData", label: "ExtraData" },
	{ key: "Addresses", label: "Alamat" },
	{ key: "Businesses", label: "Pekerjaan" },
	{ key: "Academics", label: "Pendidikan" },
	{ key: "Relations", label: "Relasi" },
	{ key: "WebUsers", label: "Emails" },
];

/* ------------------------------------------------------------------ */
/*  Matching: Sørensen–Dice bigram similarity for names,              */
/*  exact / near-miss comparison for graduation year.                 */
/* ------------------------------------------------------------------ */
function normalize(str: string): string {
	return str.trim().toLowerCase().replace(/\s+/g, " ");
}

function bigrams(str: string): string[] {
	const s = normalize(str).replace(/[^a-z0-9 ]/g, "");
	const grams: string[] = [];
	for (let i = 0; i < s.length - 1; i++) grams.push(s.slice(i, i + 2));
	return grams;
}

function diceSimilarity(a: string, b: string): number {
	const A = bigrams(a);
	const B = bigrams(b);
	if (A.length === 0 || B.length === 0) return normalize(a) === normalize(b) ? 1 : 0;
	const bag = new Map<string, number>();
	A.forEach((g) => bag.set(g, (bag.get(g) ?? 0) + 1));
	let overlap = 0;
	B.forEach((g) => {
		const count = bag.get(g) ?? 0;
		if (count > 0) {
			overlap++;
			bag.set(g, count - 1);
		}
	});
	return (2 * overlap) / (A.length + B.length);
}

type Candidate = {
	record: Alumni;
	nameScore: number;
	yearDelta: number;
	overall: number;
};

function rankCandidates(alumniList: Alumni[], req: Alumni): Candidate[] {
	let list: Candidate[] = []
	alumniList.map((record) => {
		if (record.Id != req.Id) {
			const nameScore = diceSimilarity(req.Name, record.Name);
			const yearDelta = Math.abs(record.Graduation - req.Graduation);
			const yearScore = yearDelta === 0 ? 1 : yearDelta === 1 ? 0.6 : Math.max(0, 1 - yearDelta * 0.25);
			const overall = nameScore * 0.8 + yearScore * 0.2;
			list.push({ record, nameScore, yearDelta, overall });
		}
	});
	return list.sort((x, y) => y.overall - x.overall);
}

function confidenceTier(overall: number): { label: string; color: string; soft: string } {
	if (overall >= 0.85) return { label: "Strong match", color: theme.good, soft: theme.goodSoft };
	if (overall >= 0.6) return { label: "Possible match", color: theme.amber, soft: theme.amberSoft };
	return { label: "No confident match", color: theme.bad, soft: theme.badSoft };
}

export function ViewMergeCandidate({ selfEntry, onMerge, onCancel }: {
	selfEntry: AlumniExtended;
	onMerge: (original: AlumniExtended, tobeKeep: AlumniExtended, tobeDeleted: AlumniExtended) => void;
	onCancel: ()=> void;
}) {

	const navigate = useNavigate();
	const { setToken } = useAuth()
	const { setNotify } = useNotify();
	const { alumniList } = useIASMA();

	// const [values, setValues] = useState<AlumniExtended | null>(null)
	const [mainEntry, setMainEntry] = useState<AlumniExtended | null>(null)
	const [candidateYear, setCandidateYear] = useState<number>(selfEntry?.Graduation ?? 0)
	const [candidatePool, setCandidatePool] = useState<Alumni[]>(alumniList ?? [])

	useEffect(() => {
		setCandidateYear(selfEntry?.Graduation ?? 0)
	}, [selfEntry?.Graduation])

	useEffect(() => {
		if (!candidateYear || candidateYear <= 0) {
			setCandidatePool(alumniList ?? [])
			return;
		}

		let cancelled = false;
		const fetchCandidatesByYear = async () => {
			try {
				setToken(axios);
				const recs: Alumni[] = await queryAlumniList(candidateYear);
				if (!cancelled) setCandidatePool(recs);
			} catch (error: any) {
				if (!cancelled) {
					setNotify({ title: "Error Fetching Candidate List", error, severity: 'error' });
				}
			}
		};

		fetchCandidatesByYear();
		return () => { cancelled = true; }
	}, [candidateYear, setToken, setNotify])

	const candidates = useMemo(() => {
		if (candidatePool.length > 0) {
			return rankCandidates(candidatePool, selfEntry);
		}
		return rankCandidates(alumniList ?? [], selfEntry);
	}, [candidatePool, alumniList, selfEntry]);

	const onSelectCandidate = async (candidate: Alumni) => {
		try {
			setNotify(null);
			setToken(axios);
			const mainRecord: AlumniExtended = await queryAlumni(candidate.Id);
			setMainEntry(mainRecord);
			// setValues(mainRecord);
		} catch (error: any) {
			setNotify({ title: "Error Fetching Records", error, severity: 'error' });
		}
	}

	function showCandicates() {
		let elements = [];
		if (candidates.length > 0) {
			let legibleCandidates = candidates.filter(c => c.overall >= 0.3).slice(0, 10);
			for (let i = 0; i < legibleCandidates.length; i++) {
				const candidate = legibleCandidates[i];
				elements.push(<TableRow key={i} onClick={() => onSelectCandidate(candidate.record)} className="text-sm">
					<TableCell>{candidate.record.Name}</TableCell>
					<TableCell>{candidate.record.Graduation}</TableCell>
					<TableCell>{Math.round(candidate.overall * 100)}% · {confidenceTier(candidate.overall).label}</TableCell>
				</TableRow>);
			}
			if (elements.length > 0)
				return (
					<div className="overflow-x-auto">
						<Table className="w-full border-collapse">
							<TableHeader>
								<TableRow>
									<TableHead>Nama</TableHead>
									<TableHead>Angkatan</TableHead>
									<TableHead>Kesesuaian</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{elements}
							</TableBody>
						</Table>
					</div>
				)
		}
	}

	return (
		<div>
			{!mainEntry &&
				<div className="rounded-lg p-0.5 sm:p-4" style={{ backgroundColor: theme.card, border: `1px solid ${theme.line}` }}>
					<div className="mb-6 p-1 flex flex-row gap-2 sm:items-start justify-between">
						<div className="min-w-0">
							<h2 className="mb-1 text-xl" style={{ color: theme.ink, fontFamily: "'Source Serif 4', serif", fontWeight: 600 }}>
								{selfEntry.Name}
							</h2>
							<div className="flex items-center gap-2 text-sm" style={{ color: theme.inkSoft }}>
								<div className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white/80 px-2 py-1">
									<button
										type="button"
										onClick={() => setCandidateYear((prev) => Math.max(0, prev - 1))}
										className="flex h-6 w-6 items-center justify-center rounded border border-slate-200 bg-slate-50 text-slate-600 transition hover:bg-slate-100"
										aria-label="Kurangi angkatan"
									>
										<ChevronDown size={12} />
									</button>
									<div className="flex items-center gap-1.5">
										<GraduationCap size={13} />
										<span>{candidateYear}</span>
									</div>
									<button
										type="button"
										onClick={() => setCandidateYear((prev) => prev + 1)}
										className="flex h-6 w-6 items-center justify-center rounded border border-slate-200 bg-slate-50 text-slate-600 transition hover:bg-slate-100"
										aria-label="Tambah angkatan"
									>
										<ChevronUp size={12} />
									</button>
								</div>
							</div>
							{selfEntry.Classes && (
								<div className="flex items-center gap-1.5 text-sm" style={{ color: theme.inkSoft }}>
									<School size={13} />
									{selfEntry.Classes}
								</div>
							)}
						</div>
						<div>
							<button className="bg-amber-300 px-2 py-1.5 rounded-lg cursor-pointer inline-flex items-center gap-1.5 text-xs" onClick={onCancel}>
								<X size={16} />
								<span>Batal</span>
							</button>
						</div>
					</div>
					<h3 className="mb-1 text-sm text-center" style={{ color: theme.ink, fontWeight: 500, fontStyle: "italic" }}>
						Pilih record dari tabel sebagai data utama
					</h3>
					{showCandicates()}
				</div>
			}
			{/* ------------------------------------------------------------------------------------------------------------------------------------------------ */}
			{ mainEntry && <ViewMerge main={mainEntry} other={selfEntry} onCancel={onCancel} onMerge={onMerge} />}
		</div>
	)

}